import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Info } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import {
  entityEditTabBarWrapClassName,
  entityEditTabButtonClassName,
  entityEditTabListClassName,
} from '@/components/ui/entityEditTabNavStyles'
import { cn } from '@/lib/utils/helpers'
import { showApiError } from '@/lib/utils/notifications'
import {
  countActiveCronMonitorFilters,
  CronMonitorFilters,
  DEFAULT_CRON_MONITOR_LIST_FILTERS,
} from '@/features/cron-monitor/components/CronMonitorFilters'
import { CronMonitorSortColumnHeader } from '@/features/cron-monitor/components/CronMonitorSortHeader'
import type {
  CronMonitorFailedTableProps,
  CronMonitorListFilters,
  CronMonitorMissedTableProps,
  CronMonitorQueueTableProps,
  CronMonitorSortDir,
  CronMonitorSuccessfulTableProps,
} from '@/features/cron-monitor/types'
import {
  getCronMonitor,
  getCronMonitorFailedJob,
  getCronMonitorFailedJobs,
  getCronMonitorMissedJobs,
  getCronMonitorRunningJobs,
  getCronMonitorStuckJobs,
  getCronMonitorWaitingJobs,
  getCronMonitorSuccessfulJobs,
} from '@/features/jobs/services/jobsService'
import type {
  CronMonitorFailedJob,
  CronMonitorFailedJobDetail,
  CronMonitorFailedJobsPage,
  CronMonitorListKind,
  CronMonitorMissedJob,
  CronMonitorMissedJobsPage,
  CronMonitorQueueJob,
  CronMonitorQueueJobsPage,
  CronMonitorSuccessfulJob,
  CronMonitorSuccessfulJobsPage,
  CronMonitorTimeRange,
} from '@/features/jobs/types'
import { ROUTE_PATHS } from '@/config/routes'

const PER_PAGE_OPTIONS = [15, 30, 50, 100] as const
const DEFAULT_PER_PAGE = 15
const ROW_CLASS = 'border-t border-slate-100 odd:bg-white even:bg-[#f8f9fb]'
const CELL_CLASS = 'px-4 py-2 text-left text-slate-700'

const KIND_META: Record<
  CronMonitorListKind,
  {
    title: string
    subtitle: string
    empty: string
    searchPlaceholder: string
  }
> = {
  failed: {
    title: 'Failed jobs',
    subtitle: 'All failed queue jobs for the selected time range',
    empty: 'No failed jobs found.',
    searchPlaceholder: 'Search failed jobs...',
  },
  missed: {
    title: 'Missed jobs',
    subtitle: 'Scheduled jobs that did not run when expected',
    empty: 'No missed jobs found.',
    searchPlaceholder: 'Search missed jobs...',
  },
  stuck: {
    title: 'Stuck jobs',
    subtitle: 'Queue jobs reserved for more than 30 minutes',
    empty: 'No stuck jobs found.',
    searchPlaceholder: 'Search stuck jobs...',
  },
  waiting: {
    title: 'Waiting jobs',
    subtitle: 'Queue jobs not yet reserved by a worker',
    empty: 'No waiting jobs found.',
    searchPlaceholder: 'Search waiting jobs...',
  },
  running: {
    title: 'Running jobs',
    subtitle: 'Queue jobs currently reserved by a worker',
    empty: 'No running jobs found.',
    searchPlaceholder: 'Search running jobs...',
  },
  successful: {
    title: 'Successful jobs',
    subtitle: 'Completed cron runs for the selected time range',
    empty: 'No successful jobs found.',
    searchPlaceholder: 'Search successful jobs...',
  },
}

function isListKind(value: string | null | undefined): value is CronMonitorListKind {
  return (
    value === 'failed'
    || value === 'missed'
    || value === 'stuck'
    || value === 'waiting'
    || value === 'running'
    || value === 'successful'
  )
}

function timestampMs(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null
  if (typeof value === 'number') return value < 10_000_000_000 ? value * 1000 : value
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed
}

function formatDateTime(value: string | number | null | undefined): string {
  const timestamp = timestampMs(value)
  if (timestamp == null) return '—'
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDurationSeconds(secondsValue: number): string {
  const seconds = Math.max(0, Math.floor(secondsValue))
  if (seconds >= 86400) return `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h`
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  if (seconds >= 60) return `${Math.floor(seconds / 60)}m`
  return `${seconds}s`
}

function formatDurationFrom(value: string | number | null | undefined): string {
  const timestamp = timestampMs(value)
  if (timestamp == null) return '—'
  return formatDurationSeconds(Math.max(0, Math.floor((Date.now() - timestamp) / 1000)))
}

function includesText(haystack: string | null | undefined, needle: string): boolean {
  if (!needle.trim()) return true
  return (haystack ?? '').toLowerCase().includes(needle.trim().toLowerCase())
}

function matchesQueueType(
  queue: string | null | undefined,
  selected: CronMonitorListFilters['queue'],
): boolean {
  if (!selected) return true
  return (queue ?? '') === selected
}

function formatTypeLabel(queue: string | null | undefined): string {
  if (!queue) return '—'
  if (queue === 'onDemand') return 'On demand'
  if (queue === 'default') return 'Default'
  if (queue === 'otp') return 'OTP'
  return queue
}

function listCacheKey(
  kind: CronMonitorListKind,
  page: number,
  perPage: number,
  timeRange: CronMonitorTimeRange,
): string {
  const range = kind === 'failed' || kind === 'successful' ? timeRange : '-'
  return `${kind}:${page}:${perPage}:${range}`
}

function compareValues(left: unknown, right: unknown, dir: CronMonitorSortDir): number {
  const a = left == null ? '' : left
  const b = right == null ? '' : right
  let result = 0
  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b
  } else {
    const aTime = timestampMs(a as string | number)
    const bTime = timestampMs(b as string | number)
    if (aTime != null && bTime != null) {
      result = aTime - bTime
    } else {
      result = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
    }
  }
  return dir === 'asc' ? result : -result
}

type PageState =
  | CronMonitorFailedJobsPage
  | CronMonitorMissedJobsPage
  | CronMonitorQueueJobsPage
  | CronMonitorSuccessfulJobsPage

export function CronMonitorJobsListPage() {
  const navigate = useNavigate()
  const { kind: kindParam } = useParams<{ kind: string }>()
  const [searchParams] = useSearchParams()
  const kindFromQuery = searchParams.get('kind')
  const resolvedKind = isListKind(kindFromQuery)
    ? kindFromQuery
    : isListKind(kindParam)
      ? kindParam
      : null
  const kind: CronMonitorListKind | null = resolvedKind ?? (
    // Unified list route defaults to failed when no kind is provided
    kindParam == null && kindFromQuery == null ? 'failed' : null
  )

  const timeRangeParam = searchParams.get('time_range')
  const initialTimeRange: CronMonitorTimeRange =
    timeRangeParam === '7d' || timeRangeParam === '30d' || timeRangeParam === 'all'
      ? timeRangeParam
      : '24h'

  const pageParam = Number(searchParams.get('page') || '1')
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1

  const perPageRaw = Number(searchParams.get('per_page') || String(DEFAULT_PER_PAGE))
  const perPage = (PER_PAGE_OPTIONS as readonly number[]).includes(perPageRaw)
    ? perPageRaw
    : DEFAULT_PER_PAGE

  const [list, setList] = useState<PageState | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [errorDetail, setErrorDetail] = useState<CronMonitorFailedJobDetail | null>(null)
  const [errorDetailLoading, setErrorDetailLoading] = useState(false)
  const [orderBy, setOrderBy] = useState('')
  const [sortBy, setSortBy] = useState<CronMonitorSortDir>('asc')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [filters, setFilters] = useState<CronMonitorListFilters>({
    ...DEFAULT_CRON_MONITOR_LIST_FILTERS,
    time_range: initialTimeRange,
  })
  const [tabCounts, setTabCounts] = useState<Partial<Record<CronMonitorListKind, number>>>({})
  const listCacheRef = useRef<Partial<Record<CronMonitorListKind, { key: string; data: PageState }>>>({})

  const meta = kind ? KIND_META[kind] : null
  const usesTimeRange = kind === 'failed' || kind === 'successful'
  const timeRange = usesTimeRange ? filters.time_range : '24h'

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const monitor = await getCronMonitor(1, filters.time_range)
        if (cancelled) return
        setTabCounts({
          failed: monitor.summary.failed,
          missed: monitor.summary.missed,
          stuck: monitor.summary.stuck,
          waiting: monitor.summary.queued,
          running: monitor.summary.running,
          successful: monitor.summary.successful,
        })
      } catch {
        // Keep last known counts; list load errors surface separately.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [filters.time_range])

  const goToList = useCallback((nextKind: CronMonitorListKind, extras?: Record<string, string>) => {
    const next = new URLSearchParams()
    next.set('kind', nextKind)
    Object.entries(extras ?? {}).forEach(([key, value]) => {
      if (value) next.set(key, value)
    })
    if (!next.has('per_page')) {
      next.set('per_page', String(perPage))
    }
    navigate(`${ROUTE_PATHS.DASHBOARD_JOBS_CRON_MONITOR_LIST}?${next.toString()}`)
  }, [navigate, perPage])

  const setKind = (nextKind: CronMonitorListKind) => {
    const extras: Record<string, string> = {
      page: '1',
      per_page: String(perPage),
    }
    if (nextKind === 'failed' || nextKind === 'successful') {
      extras.time_range = filters.time_range || '24h'
    }
    startTransition(() => {
      goToList(nextKind, extras)
    })
  }

  const load = useCallback(async () => {
    if (!kind) return
    const cacheKey = listCacheKey(kind, page, perPage, timeRange)
    const cached = listCacheRef.current[kind]
    if (cached?.key === cacheKey) {
      setList(cached.data)
      setLoading(false)
    } else {
      setList(null)
      setLoading(true)
    }
    setRefreshing(true)
    try {
      let next: PageState
      if (kind === 'failed') {
        next = await getCronMonitorFailedJobs(page, perPage, timeRange)
      } else if (kind === 'missed') {
        next = await getCronMonitorMissedJobs(page, perPage)
      } else if (kind === 'stuck') {
        next = await getCronMonitorStuckJobs(page, perPage)
      } else if (kind === 'waiting') {
        next = await getCronMonitorWaitingJobs(page, perPage)
      } else if (kind === 'successful') {
        next = await getCronMonitorSuccessfulJobs(page, perPage, timeRange)
      } else {
        next = await getCronMonitorRunningJobs(page, perPage)
      }
      listCacheRef.current[kind] = { key: cacheKey, data: next }
      setList(next)
    } catch (error) {
      showApiError(error)
      setList(null)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [kind, page, perPage, timeRange])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (!kind || !list) return
    setTabCounts((prev) => (
      prev[kind] === list.total ? prev : { ...prev, [kind]: list.total }
    ))
  }, [kind, list])

  useEffect(() => {
    setOrderBy('')
    setSortBy('asc')
    setSearchInput('')
    setFilters({
      ...DEFAULT_CRON_MONITOR_LIST_FILTERS,
      time_range: initialTimeRange,
    })
    setFiltersOpen(false)
  }, [kind]) // eslint-disable-line react-hooks/exhaustive-deps -- reset UI when list kind changes

  const setPage = (nextPage: number) => {
    if (!kind) return
    const extras: Record<string, string> = {
      page: String(nextPage),
      per_page: String(perPage),
    }
    if (usesTimeRange) extras.time_range = timeRange
    goToList(kind, extras)
  }

  const changePerPage = (nextPerPage: number) => {
    if (!kind) return
    const extras: Record<string, string> = {
      page: '1',
      per_page: String(nextPerPage),
    }
    if (usesTimeRange) extras.time_range = timeRange
    goToList(kind, extras)
  }

  const syncTimeRangeInUrl = (nextRange: CronMonitorTimeRange) => {
    if (!kind) return
    goToList(kind, {
      time_range: nextRange,
      page: '1',
      per_page: String(perPage),
    })
  }

  const onSort = (field: string) => {
    if (orderBy === field) {
      setSortBy((prev) => (prev === 'asc' ? 'desc' : 'asc'))
      return
    }
    setOrderBy(field)
    setSortBy('asc')
  }

  const applyFilters = (nextFilters: CronMonitorListFilters) => {
    setFilters(nextFilters)
    if (usesTimeRange && nextFilters.time_range !== timeRange) {
      syncTimeRangeInUrl(nextFilters.time_range)
    }
  }

  const resetFilters = () => {
    setFilters({
      ...DEFAULT_CRON_MONITOR_LIST_FILTERS,
      time_range: usesTimeRange ? '24h' : filters.time_range,
    })
    if (usesTimeRange) syncTimeRangeInUrl('24h')
  }

  const openFullError = async (failedJobId: number) => {
    setErrorDetailLoading(true)
    try {
      setErrorDetail(await getCronMonitorFailedJob(failedJobId))
    } catch (error) {
      showApiError(error)
    } finally {
      setErrorDetailLoading(false)
    }
  }

  const effectiveFilters = useMemo(
    () => ({ ...filters, job_name: searchInput }),
    [filters, searchInput],
  )

  const activeFilterCount = kind
    ? countActiveCronMonitorFilters(filters, kind)
    : 0

  const rows = useMemo(() => list?.data ?? [], [list])

  const filteredSortedRows = useMemo(() => {
    if (!kind) return []

    let next = [...rows] as unknown as Array<Record<string, unknown>>

    if (kind === 'failed') {
      next = next.filter((row) => {
        const job = row as unknown as CronMonitorFailedJob
        return (
          includesText(job.job_name, effectiveFilters.job_name)
          && matchesQueueType(job.queue, effectiveFilters.queue)
        )
      })
      if (orderBy) {
        next.sort((a, b) => {
          const left = a as unknown as CronMonitorFailedJob
          const right = b as unknown as CronMonitorFailedJob
          const map: Record<string, unknown> = {
            job_name: left.job_name,
            failed_at: left.failed_at,
            duration: left.duration_ms ?? -1,
            error: left.error_summary,
          }
          const mapB: Record<string, unknown> = {
            job_name: right.job_name,
            failed_at: right.failed_at,
            duration: right.duration_ms ?? -1,
            error: right.error_summary,
          }
          return compareValues(map[orderBy], mapB[orderBy], sortBy)
        })
      }
    } else if (kind === 'missed') {
      next = next.filter((row) => {
        const job = row as unknown as CronMonitorMissedJob
        return includesText(job.job_name, effectiveFilters.job_name)
      })
      if (orderBy) {
        next.sort((a, b) => {
          const left = a as unknown as CronMonitorMissedJob
          const right = b as unknown as CronMonitorMissedJob
          const map: Record<string, unknown> = {
            job_name: left.job_name,
            expected_at: left.expected_at,
            overdue: left.overdue_seconds,
            last_run: left.last_run_at ?? '',
          }
          const mapB: Record<string, unknown> = {
            job_name: right.job_name,
            expected_at: right.expected_at,
            overdue: right.overdue_seconds,
            last_run: right.last_run_at ?? '',
          }
          return compareValues(map[orderBy], mapB[orderBy], sortBy)
        })
      }
    } else if (kind === 'stuck' || kind === 'running' || kind === 'waiting') {
      next = next.filter((row) => {
        const job = row as unknown as CronMonitorQueueJob
        return (
          includesText(job.job_name, effectiveFilters.job_name)
          && matchesQueueType(job.queue, effectiveFilters.queue)
        )
      })
      if (orderBy) {
        next.sort((a, b) => {
          const left = a as unknown as CronMonitorQueueJob
          const right = b as unknown as CronMonitorQueueJob
          const timeField = kind === 'waiting'
            ? (left.available_at ?? left.created_at)
            : left.reserved_at
          const timeFieldB = kind === 'waiting'
            ? (right.available_at ?? right.created_at)
            : right.reserved_at
          const map: Record<string, unknown> = {
            job_name: left.job_name,
            queue: left.queue,
            reserved_at: timeField,
            duration: timestampMs(timeField) ?? 0,
            attempts: left.attempts ?? 0,
          }
          const mapB: Record<string, unknown> = {
            job_name: right.job_name,
            queue: right.queue,
            reserved_at: timeFieldB,
            duration: timestampMs(timeFieldB) ?? 0,
            attempts: right.attempts ?? 0,
          }
          return compareValues(map[orderBy], mapB[orderBy], sortBy)
        })
      }
    } else if (kind === 'successful') {
      next = next.filter((row) => {
        const job = row as unknown as CronMonitorSuccessfulJob
        return (
          includesText(job.job_name, effectiveFilters.job_name)
          && matchesQueueType(job.queue, effectiveFilters.queue)
        )
      })
      if (orderBy) {
        next.sort((a, b) => {
          const left = a as unknown as CronMonitorSuccessfulJob
          const right = b as unknown as CronMonitorSuccessfulJob
          const map: Record<string, unknown> = {
            job_name: left.job_name,
            completed_at: left.completed_at ?? left.started_at,
            duration: left.duration_ms ?? -1,
            queue: left.queue ?? '',
            status: 'successful',
          }
          const mapB: Record<string, unknown> = {
            job_name: right.job_name,
            completed_at: right.completed_at ?? right.started_at,
            duration: right.duration_ms ?? -1,
            queue: right.queue ?? '',
            status: 'successful',
          }
          return compareValues(map[orderBy], mapB[orderBy], sortBy)
        })
      }
    }

    return next
  }, [effectiveFilters, kind, orderBy, rows, sortBy])

  if (!kind || !meta) {
    return (
      <PageShell title="CRON MONITOR" contentClassName="px-3 py-4 sm:px-5 sm:py-5">
        <p className="text-sm text-slate-600">Unknown job list.</p>
      </PageShell>
    )
  }

  return (
    <PageShell
      title="CRON MONITOR"
      pageHeaderTestIdRoot={`cron-monitor-${kind}-list-header`}
      contentClassName="px-3 py-4 sm:px-5 sm:py-5 space-y-4"
    >
      <div>
        <h2 className="text-2xl font-bold leading-8 text-slate-900">Job listings</h2>
        <p className="mt-1 text-sm text-slate-500">
          View failed, missed, stuck, waiting, running, and successful jobs in one place
        </p>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className={entityEditTabBarWrapClassName}>
          <nav
            className={entityEditTabListClassName('start')}
            aria-label="Job listing type"
            data-testid="cron-monitor-list-tabs"
          >
            {(Object.keys(KIND_META) as CronMonitorListKind[]).map((tabKind) => {
              const count = tabCounts[tabKind]
              return (
                <button
                  key={tabKind}
                  type="button"
                  onClick={() => setKind(tabKind)}
                  className={entityEditTabButtonClassName(kind === tabKind, { align: 'start' })}
                  aria-current={kind === tabKind ? 'page' : undefined}
                >
                  <span>{KIND_META[tabKind].title.replace(/ jobs$/i, '')}</span>
                  {count != null ? (
                    <span
                      className={cn(
                        'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                        kind === tabKind
                          ? 'bg-white/80 text-button'
                          : 'bg-slate-100 text-slate-600',
                      )}
                    >
                      {count}
                    </span>
                  ) : null}
                </button>
              )
            })}
          </nav>
        </div>

        <div className="space-y-2 border-t border-slate-100 px-4 py-3 sm:px-6">
          <div>
            <h3 className="text-base font-semibold text-slate-900">{meta.title}</h3>
            <p className="text-xs text-slate-500">{meta.subtitle}</p>
          </div>

          <ListSearchToolbar
            testIdPrefix={`cron-monitor-${kind}`}
            searchValue={searchInput}
            searchPlaceholder={meta.searchPlaceholder}
            onSearchChange={setSearchInput}
            onOpenFilters={() => setFiltersOpen(true)}
            activeFilterCount={activeFilterCount}
            hideFilters={kind === 'missed'}
            className="!gap-2 sm:!gap-3"
          />
        </div>

        {list ? (
          <ListPaginationBar
            placement="header"
            currentPage={list.current_page}
            lastPage={list.last_page}
            totalRecords={list.total}
            perPage={list.per_page}
            onPageChange={setPage}
            onPerPageChange={changePerPage}
            perPageOptions={[...PER_PAGE_OPTIONS]}
            showPerPageSelect
            summaryLayout="angular"
            testIdPrefix={`cron-monitor-${kind}`}
          />
        ) : null}

        <div
          className={cn(
            'overflow-x-auto transition-opacity duration-200',
            refreshing && list ? 'opacity-60' : 'opacity-100',
          )}
        >
          {kind === 'failed' ? (
            <FailedTable
              loading={loading && !list}
              rows={filteredSortedRows as unknown as CronMonitorFailedJob[]}
              empty={meta.empty}
              errorDetailLoading={errorDetailLoading}
              onViewError={(id) => void openFullError(id)}
              orderBy={orderBy}
              sortBy={sortBy}
              onSort={onSort}
            />
          ) : null}
          {kind === 'missed' ? (
            <MissedTable
              loading={loading && !list}
              rows={filteredSortedRows as unknown as CronMonitorMissedJob[]}
              empty={meta.empty}
              orderBy={orderBy}
              sortBy={sortBy}
              onSort={onSort}
            />
          ) : null}
          {kind === 'stuck' || kind === 'running' || kind === 'waiting' ? (
            <QueueTable
              kind={kind}
              loading={loading && !list}
              rows={filteredSortedRows as unknown as CronMonitorQueueJob[]}
              empty={meta.empty}
              orderBy={orderBy}
              sortBy={sortBy}
              onSort={onSort}
            />
          ) : null}
          {kind === 'successful' ? (
            <SuccessfulTable
              loading={loading && !list}
              rows={filteredSortedRows as unknown as CronMonitorSuccessfulJob[]}
              empty={meta.empty}
              orderBy={orderBy}
              sortBy={sortBy}
              onSort={onSort}
            />
          ) : null}
        </div>

        {list ? (
          <ListPaginationBar
            placement="footer"
            currentPage={list.current_page}
            lastPage={list.last_page}
            totalRecords={list.total}
            perPage={list.per_page}
            onPageChange={setPage}
            onPerPageChange={changePerPage}
            perPageOptions={[...PER_PAGE_OPTIONS]}
            showPerPageSelect
            summaryLayout="angular"
            testIdPrefix={`cron-monitor-${kind}`}
          />
        ) : null}
      </div>

      <CronMonitorFilters
        kind={kind}
        filters={filters}
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      />

      <OnScreenModal
        isOpen={Boolean(errorDetail)}
        onClose={() => setErrorDetail(null)}
        title={errorDetail ? `Error — ${errorDetail.job_name}` : 'Error detail'}
        maxWidth="max-w-900"
        bodyScrollOnly
        bodyClassName="max-h-[70vh]"
      >
        {errorDetail ? (
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded-lg bg-slate-50 p-3 text-xs text-slate-800">
            {errorDetail.exception}
          </pre>
        ) : null}
      </OnScreenModal>
    </PageShell>
  )
}

function FailedTable({
  loading,
  rows,
  empty,
  errorDetailLoading,
  onViewError,
  orderBy,
  sortBy,
  onSort,
}: CronMonitorFailedTableProps) {
  return (
    <table className="w-full min-w-[820px] text-left text-xs">
      <thead className="bg-slate-50 text-slate-500">
        <tr>
          <CronMonitorSortColumnHeader field="job_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Job
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="failed_at" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Failed at
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="duration" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Duration
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="error" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Error
          </CronMonitorSortColumnHeader>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-slate-500">Loading…</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-slate-500">{empty}</td>
          </tr>
        ) : (
          rows.map((job) => (
            <tr key={job.id} className={ROW_CLASS}>
              <td className={cn(CELL_CLASS, 'font-semibold text-slate-800')}>{job.job_name}</td>
              <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>{formatDateTime(job.failed_at)}</td>
              <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                {job.duration_ms == null ? '—' : formatDurationSeconds(job.duration_ms / 1000)}
              </td>
              <td className={cn(CELL_CLASS, 'max-w-[360px]')}>
                <div className="flex min-w-0 items-center justify-start gap-2">
                  <span className="truncate">{job.error_summary || 'Queue job failed'}</span>
                  <button
                    type="button"
                    onClick={() => onViewError(job.id)}
                    disabled={errorDetailLoading}
                    className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-sky-200 bg-sky-50 text-[#1597ff] transition hover:bg-sky-100 disabled:opacity-50"
                    aria-label={`View full error for ${job.job_name}`}
                    title="View full error"
                  >
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

function MissedTable({
  loading,
  rows,
  empty,
  orderBy,
  sortBy,
  onSort,
}: CronMonitorMissedTableProps) {
  return (
    <table className="w-full min-w-[820px] text-left text-xs">
      <thead className="bg-slate-50 text-slate-500">
        <tr>
          <CronMonitorSortColumnHeader field="job_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Job
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="expected_at" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Expected at
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="overdue" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Overdue
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="last_run" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Last run
          </CronMonitorSortColumnHeader>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-slate-500">Loading…</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-slate-500">{empty}</td>
          </tr>
        ) : (
          rows.map((job) => (
            <tr key={job.schedule_key} className={ROW_CLASS}>
              <td className={cn(CELL_CLASS, 'font-semibold text-slate-800')}>{job.job_name}</td>
              <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>{formatDateTime(job.expected_at)}</td>
              <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                {formatDurationSeconds(job.overdue_seconds)}
              </td>
              <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                {job.last_run_at ? formatDateTime(job.last_run_at) : 'No recorded run'}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}

function QueueTable({
  kind,
  loading,
  rows,
  empty,
  orderBy,
  sortBy,
  onSort,
}: CronMonitorQueueTableProps) {
  const timeLabel = kind === 'waiting' ? 'Queued' : 'Reserved'
  return (
    <table className="w-full min-w-[820px] text-left text-xs">
      <thead className="bg-slate-50 text-slate-500">
        <tr>
          <CronMonitorSortColumnHeader field="job_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Job
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="queue" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Queue
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="reserved_at" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            {timeLabel}
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="duration" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Duration
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="attempts" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Attempts
          </CronMonitorSortColumnHeader>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading…</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={5} className="px-4 py-8 text-center text-slate-500">{empty}</td>
          </tr>
        ) : (
          rows.map((job) => {
            const timeValue = kind === 'waiting'
              ? (job.available_at ?? job.created_at)
              : job.reserved_at
            return (
              <tr key={job.id} className={ROW_CLASS}>
                <td className={cn(CELL_CLASS, 'font-semibold text-slate-800')}>{job.job_name}</td>
                <td className={CELL_CLASS}>{job.queue || '—'}</td>
                <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>{formatDateTime(timeValue)}</td>
                <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>{formatDurationFrom(timeValue)}</td>
                <td className={CELL_CLASS}>{job.attempts ?? 0}</td>
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  )
}

function SuccessfulTable({
  loading,
  rows,
  empty,
  orderBy,
  sortBy,
  onSort,
}: CronMonitorSuccessfulTableProps) {
  return (
    <table className="w-full min-w-[820px] text-left text-xs">
      <thead className="bg-slate-50 text-slate-500">
        <tr>
          <CronMonitorSortColumnHeader field="job_name" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Job
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="completed_at" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Completed
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="duration" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Duration
          </CronMonitorSortColumnHeader>
          <CronMonitorSortColumnHeader field="queue" orderBy={orderBy} sortBy={sortBy} onSort={onSort}>
            Type
          </CronMonitorSortColumnHeader>
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-slate-500">Loading…</td>
          </tr>
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={4} className="px-4 py-8 text-center text-slate-500">{empty}</td>
          </tr>
        ) : (
          rows.map((job) => {
            const typeLabel = formatTypeLabel(job.queue)
            return (
              <tr key={job.id} className={ROW_CLASS}>
                <td className={cn(CELL_CLASS, 'font-semibold text-slate-800')}>{job.job_name}</td>
                <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                  {formatDateTime(job.completed_at ?? job.started_at)}
                </td>
                <td className={cn(CELL_CLASS, 'whitespace-nowrap')}>
                  {job.duration_ms == null ? '—' : formatDurationSeconds(job.duration_ms / 1000)}
                </td>
                <td className={CELL_CLASS}>
                  <span
                    className={cn(
                      'inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold',
                      typeLabel === 'On demand'
                        ? 'bg-sky-100 text-sky-700'
                        : 'bg-slate-100 text-slate-700',
                    )}
                  >
                    {typeLabel}
                  </span>
                </td>
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  )
}
